import {
  BaseConnection,
  type SessionConfig,
  type FormatConfig,
  parseFormat,
} from "./BaseConnection.js";
import { sourceInfo } from "../sourceInfo.js";
import {
  type ConfigEvent,
  isValidSocketEvent,
  type OutgoingSocketEvent,
  type IncomingSocketEvent,
} from "./events.js";
import { constructOverrides } from "./overrides.js";
import { constructEnclaveSetupConfig } from "./orchestrator.js";
import { SessionConnectionError } from "./errors.js";
import type {
  OutputEventTarget,
  OutputListener,
  OutputAudioEvent,
} from "../OutputController.js";

const MAIN_PROTOCOL = "convai";
const WSS_API_ORIGIN = "wss://api.elevenlabs.io";
const WSS_API_PATHNAME = "/v1/convai/conversation?agent_id=";

export class WebSocketConnection
  extends BaseConnection
  implements OutputEventTarget
{
  public readonly conversationId: string;
  public readonly inputFormat: FormatConfig;
  public readonly outputFormat: FormatConfig;

  private outputListeners: Set<OutputListener> = new Set();
  private pendingAudioEvents: OutputAudioEvent[] = [];

  private constructor(
    private readonly socket: WebSocket,
    conversationId: string,
    inputFormat: FormatConfig,
    outputFormat: FormatConfig
  ) {
    super();
    this.conversationId = conversationId;
    this.inputFormat = inputFormat;
    this.outputFormat = outputFormat;

    this.socket.addEventListener("error", event => {
      // In case the error event is followed by a close event, we want the
      // latter to be the one that disconnects the session as it contains more
      // useful information.
      setTimeout(
        () =>
          this.disconnect({
            reason: "error",
            message: "The connection was closed due to a socket error.",
            context: { type: event.type },
          }),
        0
      );
    });

    this.socket.addEventListener("close", event => {
      const closeCode = event.code;
      const closeReason = event.reason || undefined;
      const context = {
        type: event.type,
        code: closeCode,
        reason: closeReason,
      };
      this.disconnect(
        closeCode === 1000
          ? { reason: "agent", context, closeCode, closeReason }
          : {
              reason: "error",
              message:
                closeReason || "The connection was closed by the server.",
              context,
              closeCode,
              closeReason,
            }
      );
    });

    this.socket.addEventListener("message", event => {
      try {
        const parsedEvent = JSON.parse(event.data);
        if (!isValidSocketEvent(parsedEvent)) {
          this.debug({
            type: "invalid_event",
            message: "Received invalid socket event",
            data: event.data,
          });
          return;
        }
        this.handleMessage(parsedEvent);
      } catch (error) {
        this.debug({
          type: "parsing_error",
          message: "Failed to parse socket message",
          error: error instanceof Error ? error.message : String(error),
          data: event.data,
        });
      }
    });
  }

  public static async create(
    config: SessionConfig
  ): Promise<WebSocketConnection> {
    let socket: WebSocket | null = null;

    try {
      let url: string;
      let protocols: string[] | undefined;

      const { name: source, version } = sourceInfo;

      if (config.orchestrator) {
        // Self-hosted orchestrators don't negotiate subprotocols; requesting one makes browsers fail the connection.
        url = config.orchestrator.url;
      } else {
        const origin = config.origin ?? WSS_API_ORIGIN;

        if (config.signedUrl) {
          const separator = config.signedUrl.includes("?") ? "&" : "?";
          url = `${config.signedUrl}${separator}source=${source}&version=${version}`;
        } else {
          url = `${origin}${WSS_API_PATHNAME}${config.agentId}&source=${source}&version=${version}`;
        }

        if (config.environment) {
          url += `&environment=${encodeURIComponent(config.environment)}`;
        }

        if (config.persistentSession) {
          url += "&is_persistent=true&replay_history=true";
          const token =
            typeof config.persistentSession === "object"
              ? config.persistentSession.token
              : undefined;
          if (token) {
            url += `&persistent_session_token=${encodeURIComponent(token)}`;
          }
        }

        protocols = [MAIN_PROTOCOL];
        if (config.authorization) {
          protocols.push(`bearer.${config.authorization}`);
        }
      }
      socket = new WebSocket(url, protocols);

      const conversationConfig = await new Promise<
        ConfigEvent["conversation_initiation_metadata_event"]
      >((resolve, reject) => {
        socket!.addEventListener(
          "open",
          () => {
            if (config.orchestrator) {
              socket?.send(
                JSON.stringify(constructEnclaveSetupConfig(config.orchestrator))
              );
            }

            const overridesEvent = constructOverrides(config);

            socket?.send(JSON.stringify(overridesEvent));
          },
          { once: true }
        );

        socket!.addEventListener("error", event => {
          // In case the error event is followed by a close event, we want the
          // latter to be the one that rejects the promise as it contains more
          // useful information.
          setTimeout(
            () =>
              reject(
                new SessionConnectionError(
                  "The connection was closed due to a socket error."
                )
              ),
            0
          );
        });

        socket!.addEventListener("close", (event: CloseEvent) => {
          const message =
            event.reason ||
            (event.code === 1000
              ? "Connection closed normally before session could be established."
              : "Connection closed unexpectedly before session could be established.");
          reject(
            new SessionConnectionError(message, {
              closeCode: event.code,
              closeReason: event.reason || undefined,
            })
          );
        });

        socket!.addEventListener(
          "message",
          (event: MessageEvent) => {
            const message = JSON.parse(event.data);

            if (!isValidSocketEvent(message)) {
              return;
            }

            if (message.type === "conversation_initiation_metadata") {
              resolve(message.conversation_initiation_metadata_event);
            } else {
              console.warn(
                "First received message is not conversation metadata."
              );
            }
          },
          { once: true }
        );
      });

      const {
        conversation_id,
        agent_output_audio_format,
        user_input_audio_format,
      } = conversationConfig;

      const inputFormat = parseFormat(user_input_audio_format ?? "pcm_16000");
      const outputFormat = parseFormat(agent_output_audio_format);

      const connection = new WebSocketConnection(
        socket,
        conversation_id,
        inputFormat,
        outputFormat
      );
      // The handshake above consumed the metadata message before the
      // connection's own listener existed, so re-emit it for subscribers.
      connection.handleMessage({
        type: "conversation_initiation_metadata",
        conversation_initiation_metadata_event: conversationConfig,
      });
      return connection;
    } catch (error) {
      socket?.close();
      throw error;
    }
  }

  public close() {
    this.pendingAudioEvents = [];
    this.socket.close(1000, "User ended conversation");
  }

  public sendMessage(message: OutgoingSocketEvent) {
    this.handleOutgoingMessage(message);
    this.socket.send(JSON.stringify(message));
  }

  public addListener(listener: OutputListener): void {
    const hadListeners = this.outputListeners.size > 0;
    this.outputListeners.add(listener);
    if (hadListeners || this.pendingAudioEvents.length === 0) {
      return;
    }
    const pending = this.pendingAudioEvents;
    this.pendingAudioEvents = [];
    for (const event of pending) {
      listener(event);
    }
  }

  public removeListener(listener: OutputListener): void {
    this.outputListeners.delete(listener);
  }

  protected override handleMessage(parsedEvent: IncomingSocketEvent) {
    super.handleMessage(parsedEvent);

    // Emit audio events to output listeners
    if (parsedEvent.type === "audio" && parsedEvent.audio_event.audio_base_64) {
      const audioEvent = {
        audio_base_64: parsedEvent.audio_event.audio_base_64,
      };
      if (this.outputListeners.size === 0) {
        this.pendingAudioEvents.push(audioEvent);
        return;
      }
      this.outputListeners.forEach(listener => listener(audioEvent));
    }
  }
}
